import React, { useCallback, useEffect, useRef, useState } from "react";
import WorldFlags from "./WorldFlags";
import CatalogueCTA from "./CatalogueCTA";

/**
 * Hero - page-1 of the ledger. Layout:
 *   left column: folio number, headline (split across three lines, with one
 *                italic word in vermilion), short standfirst, two CTAs.
 *   right column: a tall rotating plate stack for port, rice, and spices.
 *   bottom: a horizontal stat-strip - three datapoints in mono.
 *
 * Imagery: Local plate photos in public/ so the hero works offline and does
 * not depend on third-party image requests at runtime.
 */

const HERO_PLATES = [
  {
    src: "/hero-port.jpg",
    alt: "Vilora Impex export cargo stacked at Mundra Port, Gujarat — Indian rice and spice exporter loading at dawn",
    plate: "PLATE I - PORT MUNDRA · 04:42",
    lineOne: "Carried by hand,",
    lineTwo: "written in ink.",
  },
  {
    src: "/hero-rice.jpg",
    alt: "Premium Indian long-grain basmati rice (1121 Sella) — sortex-cleaned, ready for export from Gujarat",
    plate: "PLATE II - PREMIUM RICE · SORTEX READY",
    lineOne: "Graded long-grain,",
    lineTwo: "packed for trust.",
  },
  {
    src: "/hero-spices.jpg",
    alt: "Indian spice exporter desk — turmeric, cumin, chilli and coriander single-origin spices from Rajkot, Gujarat",
    plate: "PLATE III - SPICE DESK · RAJKOT",
    lineOne: "Ground in colour,",
    lineTwo: "sealed in aroma.",
  },
  {
    src: "/hero-mundra-port.jpg",
    alt: "Shipping containers at Mundra Port, Gujarat — Vilora Impex outbound rice and spice export to the Gulf and Middle East",
    plate: "PLATE IV - MUNDRA PORT · SAILING",
    lineOne: "From Gujarat,",
    lineTwo: "outward by sea.",
  },
];

export default function Hero() {
  return (
    <section id="top" className="hero-section relative paper">

      {/* Printer's registration marks - top corners */}
      <CropMark className="hidden md:block absolute top-3 left-3" />
      <CropMark className="hidden md:block absolute top-3 right-3" flip />

      {/* Vertical year-rail - far left gutter */}
      <div className="hidden md:flex absolute left-3 top-32 bottom-32 flex-col items-center justify-between pointer-events-none z-10" style={{ width: 32 }}>
        <div className="rule-v" style={{ flex: 1, width: 1 }} />
        <div className="rotate-180 my-3" style={{ writingMode: "vertical-rl" }}>
          <span className="mono" style={{ fontSize: 11, letterSpacing: "0.16em", color: "var(--brass-deep)" }}>
            VOL · I · 2025 · GUJARAT
          </span>
        </div>
        <div className="rule-v" style={{ flex: 1, width: 1 }} />
      </div>

      <div className="hero-shell px-6 sm:px-14 pt-8 pb-12 md:pt-14 md:pb-24">

        {/* Folio mark - № 01, export-destination flag strip, OPENING */}
        <div className="hero-folio flex items-center gap-3 sm:gap-4 reveal reveal-d2 mb-10 md:mb-14">
          <span className="folio shrink-0">№ 01</span>
          <span className="rule shrink-0" style={{ width: 24, height: 1 }} />
          <WorldFlags />
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio shrink-0" style={{ color: "var(--ink-mute)" }}>OPENING</span>
        </div>

        {/* Two-column spread */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-stretch">

          {/* Left - typography */}
          <div className="hero-copy md:col-span-7 lg:col-span-6 flex flex-col">
            {/* Eyebrow - house mark + nameplate */}
            <div className="hero-eyebrow flex items-center gap-4 mb-8 reveal reveal-d2">
              <img
                src="/logo.png"
                alt="Vilora Impex — Indian Trading House, Gujarat"
                width={48}
                height={48}
                className="object-contain"
                style={{ filter: "drop-shadow(0 1px 0 rgba(168, 132, 61, 0.4))" }}
              />
              <div className="flex-1">
                <div className="stamp">An Indian Trading House</div>
                <div className="meta mt-1">EST · 2025 · GUJARAT</div>
              </div>
              <span className="brass-ribbon hero-season">In Season</span>
            </div>

            {/* Headline - three lines, italic accent word */}
            <h1
              className="hero-title display reveal reveal-d3"
              style={{
                fontSize: "clamp(48px, 7.6vw, 116px)",
                lineHeight: 0.92,
                letterSpacing: "-0.025em",
                fontWeight: 500,
                margin: 0,
              }}
            >
              The grain
              <br />
              of <em className="display-italic" style={{ color: "var(--vermilion)", fontWeight: 400 }}>good</em>
              <br />
              business.
            </h1>

            {/* Standfirst - short editorial paragraph */}
            <p
              className="hero-standfirst reveal reveal-d4 justify"
              style={{
                marginTop: "clamp(28px, 4vw, 44px)",
                maxWidth: "44ch",
                fontSize: "clamp(17px, 1.3vw, 20px)",
                lineHeight: 1.62,
                color: "var(--ink-soft)",
              }}
            >
              From the loamy fields of Gujarat to twelve ports across the Gulf and beyond,
              we move rice, spice, and dry goods the way they were always meant to move
              - by hand, by ledger, and on time. <em>One container, one promise.</em>
            </p>

            {/* CTAs */}
            <div className="hero-actions reveal reveal-d5 mt-10 flex flex-wrap items-center gap-6">
              <button
                onClick={() => document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-primary hero-primary"
              >
                <span className="btn-code">№ 06</span>
                <span>Open a Conversation</span>
                <span className="btn-arrow">
                  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5h10M7 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square"/>
                  </svg>
                </span>
              </button>
              <CatalogueCTA className="hero-catalogue-cta" code="CATALOGUE" compact />
            </div>

            {/* Vertical spacer that fills the rest of the column */}
            <div className="hero-spacer flex-1 min-h-[40px]" />

            {/* Stat strip */}
            <div className="hero-stats reveal reveal-d5 grid grid-cols-3 gap-6 pt-8 mt-8 border-t"
                 style={{ borderColor: "var(--rule)" }}>
              <Stat label="Ports served" value="12" suffix="+"/>
              <Stat label="Hectares contracted" value="1,400" />
              <Stat label="Container record" value="0" suffix=" missed"/>
            </div>
          </div>

          {/* Right - stacked animated plate photographs */}
          <div className="md:col-span-5 lg:col-span-6 reveal reveal-d3 -mx-6 sm:mx-0">
            <HeroImageCarousel />
          </div>
        </div>

        {/* Scroll cue - a quiet brass arrow + mono label, only on desktop */}
        <a
          href="#certifications"
          className="hero-scroll-cue hidden md:flex items-center gap-3 mt-16 group"
          aria-label="Continue reading"
        >
          <span className="mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: "var(--brass-deep)" }}>
            CONTINUE - № 02
          </span>
          <span className="hero-scroll-cue-rule" aria-hidden="true" />
          <span className="hero-scroll-cue-arrow" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v12M2 8l5 5 5-5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square" strokeLinejoin="miter"/>
            </svg>
          </span>
        </a>
      </div>
    </section>
  );
}

function HeroImageCarousel() {
  const [active, setActive] = useState(0);
  const intervalRef = useRef(null);
  const touchStartX = useRef(0);
  const isAnimating = useRef(false);
  const activePlate = HERO_PLATES[active];

  const goTo = useCallback((index) => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setActive((current) => {
      const next = typeof index === "number" ? index : (current + 1) % HERO_PLATES.length;
      return next;
    });
    window.setTimeout(() => {
      isAnimating.current = false;
    }, 900);
  }, []);

  const resetInterval = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }
    intervalRef.current = window.setInterval(() => goTo(), 3600);
  }, [goTo]);

  useEffect(() => {
    resetInterval();
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [resetInterval]);

  const choosePlate = (index) => {
    goTo(index);
    resetInterval();
  };

  return (
    <div
      className="hero-image-carousel duotone duotone--natural relative h-[360px] sm:h-[460px] md:h-full md:min-h-[640px]"
      style={{ background: "var(--paper-2)" }}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        const delta = touchStartX.current - event.changedTouches[0].clientX;
        if (Math.abs(delta) > 40) {
          goTo(delta > 0 ? undefined : (active - 1 + HERO_PLATES.length) % HERO_PLATES.length);
          resetInterval();
        }
      }}
    >
      {HERO_PLATES.map((plate, index) => {
        const isActive = index === active;

        return (
          <div
            key={plate.src}
            className={`hero-image-slide ${isActive ? "is-active" : ""}`}
            aria-hidden={!isActive}
          >
            <img
              src={plate.src}
              alt={plate.alt}
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "auto"}
              decoding="async"
            />
            <div className="hero-image-shade" />
          </div>
        );
      })}

      <div className="absolute top-5 left-5 md:top-8 md:left-8 z-20">
        <div className="hero-plate-label inline-block px-3 py-1 mono">
          {activePlate.plate}
        </div>
      </div>

      <div className="hero-image-dots" aria-label="Hero image plates">
        {HERO_PLATES.map((plate, index) => (
          <button
            key={plate.src}
            type="button"
            onClick={() => choosePlate(index)}
            aria-label={`Show ${plate.plate}`}
            aria-current={index === active}
            className={`hero-image-dot ${index === active ? "is-active" : ""}`}
          />
        ))}
      </div>

      <div key={activePlate.src} className="hero-image-caption absolute bottom-5 right-5 md:bottom-8 md:right-8 z-20 text-right">
        <div className="display-italic" style={{ color: "var(--bone)", fontSize: "clamp(20px, 2.4vw, 32px)" }}>
          {activePlate.lineOne}
        </div>
        <div className="display-italic" style={{ color: "var(--bone)", fontSize: "clamp(20px, 2.4vw, 32px)" }}>
          {activePlate.lineTwo}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, suffix }) {
  return (
    <div className="hero-stat">
      <div className="display" style={{ fontSize: "clamp(28px, 3vw, 42px)", lineHeight: 1, letterSpacing: "-0.02em" }}>
        {value}<span style={{ color: "var(--brass)", fontStyle: "italic" }}>{suffix || ""}</span>
      </div>
      <div className="meta mt-2">{label}</div>
    </div>
  );
}

/** Printer's crop / registration mark - small L in brass */
function CropMark({ className = "", flip = false }) {
  return (
    <svg
      width="22" height="22" viewBox="0 0 22 22"
      className={className}
      style={{ transform: flip ? "scaleX(-1)" : undefined }}
      aria-hidden="true"
    >
      <line x1="0" y1="11" x2="9" y2="11" stroke="var(--brass)" strokeWidth="0.6"/>
      <line x1="11" y1="0" x2="11" y2="9" stroke="var(--brass)" strokeWidth="0.6"/>
      <circle cx="11" cy="11" r="2" fill="none" stroke="var(--brass)" strokeWidth="0.6"/>
    </svg>
  );
}
